import React, { useCallback } from 'react';
import { Box, Typography, DocumentCard } from '@lucid/lego';
import { FileHubApi } from '../../../../../api/services/lupp/lupp.service';
import { downloadSfdcFileFromFilehub } from '../../../../../api/services/lupp/fileUpload/fileUpload';
import type { ModuleFileReference } from '../../../../../api/services/lupp/order/dto.order';
import { FILEHUB_MODULE_NAME } from '../../../../../constants/constants';
import styles from './DocumentsTab.module.scss';

const CATEGORY_LABELS: Record<string, string> = {
  PROFORMA_INVOICE: 'Proforma Invoice',
  FINAL_INVOICE: 'Final Invoice',
  COMMERCIAL_INVOICE: 'Commercial Invoice',
  CUSTOMS_DECLARATION: 'Customs Declaration',
  PICKUP_CHALLAN: 'Pickup Challan',
  ORDER_AGREEMENT: 'Order Agreement',
  PRODUCT_GUIDE: 'Product Guide',
  INCENTIVE_PDF: 'Incentive PDF',
  /** Per-vehicle confirmation issued once Lucid accepts a child order line. */
  ORDER_CONFIRMATION: 'Order Confirmation',
  /** Parent bulk-order document issued when the importer submits the order. */
  ORDER_ACKNOWLEDGEMENT: 'Order Acknowledgement',
  GENERIC: 'Other',
};

type DocumentsTabProps = {
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  documents?: ModuleFileReference[];
};

/**
 * A document is displayable when it can be downloaded from one of the two
 * supported sources: Filehub (`filehubIdentifier`) or Salesforce
 * (`sfdcDeliveryIdentifier` + `sfdcFileIdentifier`). SFDC-sourced files have a
 * null `filehubIdentifier`, so they must not be filtered out by it.
 */
const isDownloadable = (doc: ModuleFileReference): boolean =>
  Boolean(doc.filehubIdentifier) || Boolean(doc.sfdcDeliveryIdentifier && doc.sfdcFileIdentifier);

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  PROFORMA_INVOICE: 'documents.category.proformaInvoice',
  FINAL_INVOICE: 'documents.category.finalInvoice',
  COMMERCIAL_INVOICE: 'documents.category.commercialInvoice',
  CUSTOMS_DECLARATION: 'documents.category.customsDeclaration',
  PICKUP_CHALLAN: 'documents.category.pickupChallan',
  ORDER_AGREEMENT: 'documents.category.orderAgreement',
  PRODUCT_GUIDE: 'documents.category.productGuide',
  INCENTIVE_PDF: 'documents.category.incentivePdf',
  ORDER_CONFIRMATION: 'documents.category.orderConfirmation',
  ORDER_ACKNOWLEDGEMENT: 'documents.category.orderAcknowledgement',
  GENERIC: 'documents.category.generic',
};

const DocumentsTab: React.FC<DocumentsTabProps> = ({ theme, labels, documents = [] }) => {
  const validDocuments = documents.filter(isDownloadable);

  const handleDownload = useCallback(async (doc: ModuleFileReference) => {
    const fileName = doc.fileNameOriginal ?? 'document';
    try {
      if (doc.filehubIdentifier) {
        const response = await FileHubApi.instance.get<string>(
          `sales/X/v1/files/${doc.filehubIdentifier}/download-url`,
          { params: { module_name: FILEHUB_MODULE_NAME.ORDER_MANAGEMENT } }
        );
        const downloadUrl = response.data;

        if (downloadUrl) {
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = fileName;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          setTimeout(() => {
            if (link.parentNode) link.parentNode.removeChild(link);
          }, 100);
        }
      } else if (doc.sfdcDeliveryIdentifier && doc.sfdcFileIdentifier) {
        await downloadSfdcFileFromFilehub(
          doc.sfdcDeliveryIdentifier,
          doc.sfdcFileIdentifier,
          fileName
        );
      }
    } catch (error) {
      console.error('Failed to download file:', error);
    }
  }, []);

  const count = validDocuments.length;
  const subtitleTemplate = labels?.['documents.subtitle'];
  const defaultDocumentsCountLabel = `${count} document${count === 1 ? '' : 's'} for this order`;
  const documentsCountLabel = subtitleTemplate
    ? subtitleTemplate.replace('{{count}}', String(count))
    : defaultDocumentsCountLabel;

  return (
    <Box className={styles.root} data-testid="documents-tab" background="white">
      <Box className={styles.header}>
        <Typography variant="body" size="small" theme={theme} color="gray-4" align="right">
          {documentsCountLabel}
        </Typography>
      </Box>

      <Box className={styles.grid}>
        {validDocuments.map((doc, index) => {
          const categoryLabel = doc.documentCategory
            ? (CATEGORY_LABEL_KEYS[doc.documentCategory] &&
                labels?.[CATEGORY_LABEL_KEYS[doc.documentCategory]]) ||
              CATEGORY_LABELS[doc.documentCategory] ||
              doc.documentCategory
            : undefined;
          const date = doc.createdDatetime
            ? new Date(doc.createdDatetime).toLocaleDateString()
            : undefined;
          const eyebrow = [categoryLabel, date].filter(Boolean).join(' · ') || undefined;
          const docId =
            doc.filehubIdentifier ?? doc.fileIdentifier ?? doc.sfdcFileIdentifier ?? `doc-${index}`;

          return (
            <DocumentCard
              key={docId}
              dataTestId={`document-card-${docId}`}
              theme={theme}
              fileName={doc.fileNameOriginal ?? 'Unknown file'}
              status="success"
              eyebrow={eyebrow}
              isPreExisting
              index={index}
              onDownload={() => handleDownload(doc)}
            />
          );
        })}

        {count === 0 && (
          <Box pt={16}>
            <Typography
              variant="body"
              size="medium"
              theme={theme}
              color="gray-4"
              data-testid="documents-empty-state"
            >
              {labels?.['documents.empty'] ?? 'No documents available for this order.'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DocumentsTab;
